/**
 * AnalysisView Component
 * Ranking and analysis dashboard with filters
 */

import { useState, useMemo } from 'react'
import { Filter, ArrowUpDown, Calendar, Star } from 'lucide-react'
import { DaveScoreChart } from './DaveScoreChart'

// カテゴリフィルター
const CATEGORY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'anime', label: 'Anime' },
  { id: 'movie', label: 'Movie' },
  { id: 'event', label: 'Event' }
]

// ソート順
const SORT_OPTIONS = [
  { id: 'score_desc', label: 'Score High → Low' },
  { id: 'score_asc', label: 'Score Low → High' },
  { id: 'date_desc', label: 'Newest First' },
  { id: 'date_asc', label: 'Oldest First' }
]

/**
 * 類似スコアを計算（ユークリッド距離）
 */
function calculateSimilarity(scoreA, scoreB) {
  if (!scoreA || !scoreB) return Infinity

  const axes = ['axis_story', 'axis_visual', 'axis_excitement', 'axis_emotion', 'axis_fluency']
  let sumSquares = 0

  for (const axis of axes) {
    const diff = (scoreA[axis] || 0) - (scoreB[axis] || 0)
    sumSquares += diff * diff
  }

  return Math.sqrt(sumSquares)
}

export function AnalysisView({ animeList }) {
  const [dateRange, setDateRange] = useState({ start: '', end: '' })
  const [category, setCategory] = useState('all')
  const [sortBy, setSortBy] = useState('score_desc')
  const [selectedItem, setSelectedItem] = useState(null)

  // フィルタリングとソート
  const rankedList = useMemo(() => {
    let filtered = animeList.filter(a => a.daveScore > 0)

    // カテゴリフィルター
    if (category !== 'all') {
      filtered = filtered.filter(a => a.type === category || a.format === category)
    }

    // 日付範囲フィルター
    if (dateRange.start) {
      filtered = filtered.filter(a => {
        const date = new Date(a.lastUpdated)
        return date >= new Date(dateRange.start)
      })
    }
    if (dateRange.end) {
      filtered = filtered.filter(a => {
        const date = new Date(a.lastUpdated)
        return date <= new Date(dateRange.end)
      })
    }

    // ソート
    switch (sortBy) {
      case 'score_desc':
        filtered.sort((a, b) => b.daveScore - a.daveScore)
        break
      case 'score_asc':
        filtered.sort((a, b) => a.daveScore - b.daveScore)
        break
      case 'date_desc':
        filtered.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
        break
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.lastUpdated) - new Date(b.lastUpdated))
        break
    }

    return filtered
  }, [animeList, category, dateRange, sortBy])

  // 選択されたアイテムに類似した作品を取得
  const similarItems = useMemo(() => {
    if (!selectedItem || !selectedItem.daveScoreDetail) return []

    return animeList
      .filter(a => a.id !== selectedItem.id && a.daveScore > 0)
      .map(a => ({
        ...a,
        similarity: calculateSimilarity(selectedItem.daveScoreDetail, a.daveScoreDetail)
      }))
      .sort((a, b) => a.similarity - b.similarity)
      .slice(0, 5)
  }, [selectedItem, animeList])

  // 統計情報
  const stats = useMemo(() => {
    const scoredItems = animeList.filter(a => a.daveScore > 0)
    const avgScore = scoredItems.length > 0
      ? scoredItems.reduce((sum, a) => sum + a.daveScore, 0) / scoredItems.length
      : 0

    // スタジオ別カウント
    const studioCount = {}
    scoredItems.forEach(a => {
      const studios = a.metadata?.studios || []
      studios.forEach(studio => {
        studioCount[studio] = (studioCount[studio] || 0) + 1
      })
    })
    const topStudios = Object.entries(studioCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)

    return {
      totalScored: scoredItems.length,
      avgScore: avgScore.toFixed(1),
      topStudios
    }
  }, [animeList])

  return (
    <div>
      {/* Controls */}
      <div className="analysis-controls">
        {/* Date Range */}
        <div className="control-group">
          <span className="control-label">
            <Calendar size={12} style={{ marginRight: '4px' }} />
            Date Range
          </span>
          <div className="date-range">
            <input
              type="date"
              className="form-input"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              style={{ width: '140px' }}
            />
            <span style={{ color: 'var(--text-muted)' }}>~</span>
            <input
              type="date"
              className="form-input"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              style={{ width: '140px' }}
            />
          </div>
        </div>

        {/* Category */}
        <div className="control-group">
          <span className="control-label">
            <Filter size={12} style={{ marginRight: '4px' }} />
            Category
          </span>
          <select
            className="form-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ width: '120px' }}
          >
            {CATEGORY_FILTERS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Sort */}
        <div className="control-group">
          <span className="control-label">
            <ArrowUpDown size={12} style={{ marginRight: '4px' }} />
            Sort
          </span>
          <select
            className="form-input"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ width: '160px' }}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="anime-card" style={{ cursor: 'default' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Scored Items
          </div>
          <div className="text-gradient" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.totalScored}
          </div>
        </div>

        <div className="anime-card" style={{ cursor: 'default' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Average Score
          </div>
          <div className="text-gradient" style={{ fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.avgScore}
          </div>
        </div>

        <div className="anime-card" style={{ cursor: 'default' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Top Studios
          </div>
          <div style={{ fontSize: '0.875rem' }}>
            {stats.topStudios.length > 0
              ? stats.topStudios.map(([studio, count]) => (
                  <div key={studio} style={{ color: 'var(--text-secondary)' }}>
                    {studio}: {count}
                  </div>
                ))
              : <span style={{ color: 'var(--text-muted)' }}>No data</span>
            }
          </div>
        </div>
      </div>

      {/* Ranking List */}
      {rankedList.length > 0 ? (
        <div className="ranking-list">
          {rankedList.map((anime, index) => (
            <div
              key={anime.id}
              className="ranking-item"
              onClick={() => setSelectedItem(selectedItem?.id === anime.id ? null : anime)}
              style={{ cursor: 'pointer' }}
            >
              <div className={`ranking-number ${index < 3 ? 'top-3' : ''}`}>
                #{index + 1}
              </div>

              {anime.metadata?.imageUrl && (
                <img
                  src={anime.metadata.imageUrl}
                  alt=""
                  style={{
                    width: '50px',
                    height: '70px',
                    objectFit: 'cover',
                    borderRadius: '6px'
                  }}
                />
              )}

              <div className="ranking-info">
                <div className="ranking-title">{anime.title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {anime.metadata?.studios?.join(', ') || anime.platform || ''}
                </div>
              </div>

              <div className="ranking-score">{anime.daveScore}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <p className="empty-state-title">評価済みの作品がありません</p>
          <p style={{ fontSize: '0.875rem' }}>
            DAVE Scoreを入力して作品を評価してください
          </p>
        </div>
      )}

      {/* Similar Items Panel */}
      {selectedItem && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'var(--glass-bg)',
          borderRadius: '12px',
          border: '1px solid var(--glass-border)'
        }}>
          <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>
            <Star size={16} style={{ marginRight: '8px' }} />
            「{selectedItem.title}」と Similar VIBE
          </h3>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 250px' }}>
              <DaveScoreChart scoreDetail={selectedItem.daveScoreDetail} />
            </div>

            <div style={{ flex: 1, minWidth: '200px' }}>
              {similarItems.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {similarItems.map(item => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px',
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>{item.title}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          Score: {item.daveScore}
                        </div>
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: 'var(--accent-cyan)'
                      }}>
                        {(100 - item.similarity * 2).toFixed(0)}% match
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-muted)' }}>
                  類似作品が見つかりません
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalysisView
